import { useEffect, useState } from "react";
import api from "@/api/client";
import { Plus, Trash2, CheckCircle2, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import RecipientPicker from "@/components/RecipientPicker";

/**
 * LogItemsEditor — inline multi-row editor for received items on Inward/Outward log entries.
 *
 * Phase 5 (Feb 2026 · Gatekeeper): each row carries the recipient name + a live status check
 * (Active / Inactive / Not found) and a required decision (ACCEPT or RTO) before the parent
 * can save. On `variant="in"` only; outward retains the original streamlined UI.
 * Feb 8, 2026 · Recipient picker: replaced the free-text recipient input with a
 * company-wide directory picker for every row (allBranches=true) so gatekeepers
 * can't fat-finger a name and bypass the User Directory check.
 */
const TYPES = ["Parcel", "Document", "Letter", "Box", "Package"];
const CONDS_IN  = ["Excellent", "Good", "Damaged", "Tampered", "Wet", "Open", "Returned"];
const CONDS_OUT = ["Excellent", "Good", "Damaged", "Tampered", "Wet", "Open"];

export const emptyItem = () => ({
  item_type: "Parcel", name: "", quantity: 1, parcel_condition: "Good",
  recipient_id: "", recipient_name: "", recipient_status: null, decision: "",
  override_reason: "", rto_reason: "",
});

/** Row is "decided" when either:
 *  · user picked a recipient AND clicked ACCEPT (User ID bound to this item), OR
 *  · user clicked RTO AND provided a non-empty reason (Feb 12, 2026 hardening), OR
 *  · user clicked ACCEPT_OVERRIDE AND provided a non-empty reason.
 *  Only decided rows unlock Add-Item and the parent Save button.  */
export const isRowDecided = (r) => {
  if (!r) return false;
  if (r.decision === "RTO") return !!(r.rto_reason && r.rto_reason.trim());
  if (r.decision === "ACCEPT") return !!r.recipient_id;
  if (r.decision === "ACCEPT_OVERRIDE") return !!(r.override_reason && r.override_reason.trim());
  return false;
};
/** Convenience for parent forms — gate Save until every inward row is decided. */
export const allRowsDecided = (items) => (items || []).length > 0 && items.every(isRowDecided);

export default function LogItemsEditor({ value = [], onChange, testid = "log-items", variant = "in", onItemAdded }) {
  const isInward = variant === "in";
  const conditions = isInward ? CONDS_IN : CONDS_OUT;
  const rows = value.length > 0 ? value : [emptyItem()];
  // Add-Item lockout: for inward, block adding a new row until the last row's
  // decision is finalised (ACCEPT+bound user, or RTO). Outward stays permissive.
  const canAdd = !isInward || isRowDecided(rows[rows.length - 1]);

  const patch = (idx, key, v) => onChange(rows.map((r, i) => i === idx ? { ...r, [key]: v } : r));
  const add = () => { if (canAdd) { onChange([...rows, emptyItem()]); onItemAdded?.(); } };
  const remove = (idx) => onChange(rows.length <= 1 ? [emptyItem()] : rows.filter((_, i) => i !== idx));

  // Atomic pick + validate — avoids stale-closure races where the async status
  // patch would overwrite the freshly-picked recipient_id. Feb 8, 2026.
  const pickAndValidate = async (idx, rec, currentRows) => {
    const base = currentRows.map((row, i) => i === idx ? {
      ...row,
      recipient_id: rec.id,
      recipient_name: rec.name,
      recipient_department: rec.department || row.recipient_department,
    } : row);
    onChange(base);
    if (!isInward || !rec.name) return;
    try {
      const { data } = await api.get("/recipients/validate", { params: { name: rec.name } });
      onChange(base.map((row, i) => {
        if (i !== idx) return row;
        const suggested = row.decision || (data.status === "active" ? "ACCEPT" : "RTO");
        return {
          ...row,
          recipient_status: data.status,
          // Only auto-suggest a decision — do not overwrite an explicit user choice
          decision: suggested,
          // Feb 12, 2026 — RTO auto-sets parcel_condition to "Returned" so the
          // manifest counts it correctly (per client rule).
          parcel_condition: suggested === "RTO" ? "Returned" : row.parcel_condition,
        };
      }));
    } catch { /* ignore — user can still manually decide */ }
  };

  // Feb 12, 2026 — clicking RTO must ALSO flip parcel_condition → "Returned"
  // so the manifest/report captures it as a Returned item automatically.
  const markRto = (idx) => onChange(rows.map((r, i) => i === idx
    ? { ...r, decision: "RTO", parcel_condition: "Returned" }
    : r));

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="overline">Items received</span>
        <button type="button" onClick={add} data-testid={`${testid}-add`}
          disabled={!canAdd}
          title={canAdd ? "" : "Finish the current row (Accept a user or mark RTO) before adding a new item"}
          className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline">
          <Plus size={12}/> Add Item
        </button>
      </div>
      {rows.map((r, idx) => {
        const st = r.recipient_status;
        const stActive = st === "active";
        const stBlocked = st === "inactive" || st === "left" || st === "not_found";
        return (
        <div key={idx} className="border border-border rounded-md p-3 space-y-2 bg-muted/20" data-testid={`${testid}-row-${idx}`}>
          <div className="grid grid-cols-[130px_1fr_80px_auto] gap-2 items-start">
            <select data-testid={`${testid}-type-${idx}`} value={r.item_type}
              onChange={(e) => patch(idx, "item_type", e.target.value)}
              className="h-9 px-2 rounded-md border border-input bg-background text-xs">
              {TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <Input data-testid={`${testid}-name-${idx}`} value={r.name}
              onChange={(e) => patch(idx, "name", e.target.value)}
              placeholder="Item name / description" className="h-9 rounded-md text-xs" />
            <Input data-testid={`${testid}-qty-${idx}`} type="number" min="1" value={r.quantity}
              onChange={(e) => patch(idx, "quantity", Math.max(1, Number(e.target.value)||1))}
              className="h-9 rounded-md font-mono text-xs" />
            <button type="button" onClick={() => remove(idx)} data-testid={`${testid}-del-${idx}`}
              className="h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-primary rounded-md">
              <Trash2 size={13}/>
            </button>
          </div>
          {isInward && (
            <div className="grid grid-cols-[1fr_auto] gap-2 items-center pt-1" data-testid={`${testid}-gatekeeper-${idx}`}>
              <RecipientPicker
                value={r.recipient_id ? { id: r.recipient_id, name: r.recipient_name, department: r.recipient_department } : null}
                onChange={(rec) => pickAndValidate(idx, rec, rows)}
                allBranches={true}
                testid={`${testid}-recipient-picker-${idx}`}
                label="Select employee from directory…"
              />
              <div className="flex items-center gap-1">
                {st && (
                  <span data-testid={`${testid}-status-${idx}`}
                        className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${
                          stActive ? "border-emerald-300 bg-emerald-50 text-emerald-800" :
                          stBlocked ? "border-red-400 bg-red-50 text-red-800" :
                          "border-border bg-muted"}`}>
                    {stActive ? "User Available" : "Not Found — Inactive or Unknown"}
                  </span>
                )}
                <button type="button" data-testid={`${testid}-accept-${idx}`}
                        onClick={() => {
                          // Binding contract (Feb 8, 2026): ACCEPT locks the picked User ID
                          // to this item row. Guard against accept-without-picker.
                          if (!r.recipient_id) return;
                          patch(idx, "decision", "ACCEPT");
                        }}
                        disabled={!stActive || !r.recipient_id}
                        title={!r.recipient_id ? "Pick an employee from the directory first" : ""}
                        className={`h-8 px-2 text-[10px] font-bold uppercase tracking-widest rounded border transition
                          ${r.decision === "ACCEPT" ? "bg-emerald-600 text-white border-emerald-600"
                             : "border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white"}
                          disabled:opacity-40 disabled:cursor-not-allowed`}>
                  <CheckCircle2 size={11} className="inline mr-1"/> Accept
                </button>
                <button type="button" data-testid={`${testid}-rto-${idx}`}
                        onClick={() => markRto(idx)}
                        className={`h-8 px-2 text-[10px] font-bold uppercase tracking-widest rounded border transition
                          ${r.decision === "RTO" ? "bg-primary text-primary-foreground border-primary"
                             : "border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground"}`}>
                  <RotateCcw size={11} className="inline mr-1"/> RTO
                </button>
                <button type="button" data-testid={`${testid}-override-${idx}`}
                        onClick={() => patch(idx, "decision", "ACCEPT_OVERRIDE")}
                        title="Force-accept despite inactive/missing recipient — requires a reason"
                        className={`h-8 px-2 text-[10px] font-bold uppercase tracking-widest rounded border transition
                          ${r.decision === "ACCEPT_OVERRIDE" ? "bg-amber-600 text-white border-amber-600"
                             : "border-amber-500 text-amber-700 hover:bg-amber-600 hover:text-white"}`}>
                  Override
                </button>
              </div>
            </div>
          )}
          {isInward && r.decision === "RTO" && (
            <div className="pt-1" data-testid={`${testid}-rto-wrap-${idx}`}>
              <span className="overline text-[10px]">RTO reason <span className="text-primary">*</span></span>
              <Input data-testid={`${testid}-rto-reason-${idx}`}
                value={r.rto_reason || ""}
                onChange={(e) => patch(idx, "rto_reason", e.target.value)}
                placeholder="e.g. Address mismatch / Employee left / Wrong recipient" required
                className={`mt-1 rounded-md text-xs ${(r.rto_reason || "").trim() ? "" : "border-primary"}`}/>
              <div className="text-[9px] text-muted-foreground mt-0.5">Required — the reason is stored against this item and shown on the manifest report.</div>
            </div>
          )}
          {isInward && r.decision === "ACCEPT_OVERRIDE" && (
            <div className="pt-1" data-testid={`${testid}-override-wrap-${idx}`}>
              <span className="overline text-[10px]">Override reason <span className="text-primary">*</span></span>
              <Input data-testid={`${testid}-override-reason-${idx}`}
                value={r.override_reason || ""}
                onChange={(e) => patch(idx, "override_reason", e.target.value)}
                placeholder="e.g. Employee on leave — manager instructed to accept" required
                className={`mt-1 rounded-md text-xs ${(r.override_reason || "").trim() ? "" : "border-primary"}`}/>
              <div className="text-[9px] text-muted-foreground mt-0.5">Required for audit. Save is blocked until a reason is provided.</div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1" role="radiogroup">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mr-1">Condition</span>
            {conditions.map(c => {
              const active = r.parcel_condition === c;
              return (
                <label key={c} data-testid={`${testid}-cond-${idx}-${c}`}
                  className={`inline-flex items-center gap-1 text-xs cursor-pointer px-1.5 py-0.5 rounded ${active ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                  <input type="radio" name={`${testid}-cond-${idx}`}
                    className="accent-[#BE1E1E] h-3 w-3"
                    checked={active}
                    onChange={() => patch(idx, "parcel_condition", c)} />
                  {c}
                </label>
              );
            })}
          </div>
        </div>
      ); })}
    </div>
  );
}
